import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Search, ImageIcon, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

export function MediaLibrary({ onSelect, onClose, open }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/media", { params: q ? { q } : {} }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open, q]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this image?")) return;
    try { await api.delete(`/admin/media/${id}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-600 text-white max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-display">Media Library</DialogTitle></DialogHeader>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 flex-1">
            <Search size={15} className="text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search media..." className="bg-transparent outline-none text-sm flex-1 placeholder:text-slate-600" />
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn" className="inline-flex items-center gap-2 bg-brand text-brand-dark font-bold text-sm px-4 py-2 rounded-lg hover:bg-brand-neon transition-colors disabled:opacity-60">
            <Upload size={15} /> {uploading ? "Uploading..." : "Upload"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} />
        </div>
        <div className="overflow-y-auto thin-scroll grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pr-1">
          {items.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 text-sm"><ImageIcon size={28} className="mx-auto mb-2 opacity-50" />No media yet. Upload something.</div>}
          {items.map((m) => (
            <div key={m.id} className="group relative rounded-lg overflow-hidden border border-ink-600 aspect-square">
              <img src={m.url} alt={m.alt || m.filename} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <button onClick={() => { onSelect(m.url, m); onClose(); }} className="inline-flex items-center gap-1 bg-brand text-brand-dark font-bold text-xs px-3 py-1.5 rounded-full"><Check size={13} /> Select</button>
                <button onClick={() => del(m.id)} className="inline-flex items-center gap-1 text-red-400 text-xs"><Trash2 size={12} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImageField({ value, onChange, label = "Image", testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      {label && <label className="text-xs text-slate-400 font-mono-label">{label}</label>}
      <div className="mt-1.5 flex items-center gap-3">
        {value ? (
          <div className="relative w-20 h-16 rounded-lg overflow-hidden border border-ink-600 shrink-0">
            <img src={value} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-20 h-16 rounded-lg border border-dashed border-ink-600 flex items-center justify-center text-slate-600 shrink-0"><ImageIcon size={18} /></div>
        )}
        <div className="flex-1 flex flex-col gap-2">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="Image URL or pick from library" className="bg-ink-800 border border-ink-600 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand transition-colors" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-brand hover:text-brand-neon">Choose from library</button>
            {value && <button type="button" onClick={() => onChange("")} className="text-xs text-slate-500 hover:text-red-400">Clear</button>}
          </div>
        </div>
      </div>
      <MediaLibrary open={open} onClose={() => setOpen(false)} onSelect={(url) => onChange(url)} />
    </div>
  );
}
